import { CUSTOMER_STATUSES, CustomerStatus, MONGO_FILTER_OPERATORS } from '@vy/types';
import { CUSTOMERS_COLUMNS_KEYS } from '@vy/customers-types';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import { getStatusDisplayLabel } from '../utils/getStatusDisplayLabel';

const OPERATORS_BY_TYPE = {
  text: [{ label: 'Contains', value: MONGO_FILTER_OPERATORS.REGEX }],
  number: [
    { label: 'Equals', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Greater Than', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Less than', value: MONGO_FILTER_OPERATORS.LESS_THAN },
    { label: 'Greater than or equal', value: MONGO_FILTER_OPERATORS.GREATER_THAN_OR_EQUAL },
    { label: 'Less than or equal', value: MONGO_FILTER_OPERATORS.LESS_THAN_OR_EQUAL },
  ],
  date: [
    { label: 'Is on', value: MONGO_FILTER_OPERATORS.EQUALS },
    { label: 'Is after', value: MONGO_FILTER_OPERATORS.GREATER_THAN },
    { label: 'Is before', value: MONGO_FILTER_OPERATORS.LESS_THAN },
  ],
  select: [
    { label: 'Is any of', value: MONGO_FILTER_OPERATORS.IN },
    { label: 'Is not any of', value: MONGO_FILTER_OPERATORS.NOT_IN },
  ],
  boolean: [{ label: 'Is', value: MONGO_FILTER_OPERATORS.EQUALS }],
};

const getOperatorsByInputType = (inputType: keyof typeof OPERATORS_BY_TYPE) =>
  OPERATORS_BY_TYPE[inputType] ?? [];

const createFilterField = (
  id: string,
  label: string,
  inputType: keyof typeof OPERATORS_BY_TYPE,
  options?: { value: string; label: string }[],
): FilterField => ({
  id,
  label,
  inputType,
  operators: getOperatorsByInputType(inputType),
  ...(options && { options }),
});

const createEnumSelectField = (
  id: string,
  label: string,
  enumObject: Record<string, string | number>,
  labelMapper?: (key: string) => string,
  valueMapper?: (value: string | number) => string,
): FilterField => {
  const options = Object.entries(enumObject).map(([key, value]) => ({
    value: valueMapper ? valueMapper(value) : String(value),
    label: labelMapper ? labelMapper(key) : key,
  }));

  return createFilterField(id, label, 'select', options);
};

const createBooleanField = (id: string, label: string): FilterField =>
  createFilterField(id, label, 'boolean', [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ]);

export const CUSTOMERS_FILTER_FIELDS: FilterField[] = [
  createEnumSelectField(CUSTOMERS_COLUMNS_KEYS.STATUS, 'Status', CUSTOMER_STATUSES, (key) =>
    getStatusDisplayLabel(key as CustomerStatus),
  ),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.CREATED_AT, 'Created Date', 'date'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.UPDATED_AT, 'Updated Date', 'date'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.PARENT_CUSTOMER, 'Parent Customer', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.ADDRESS, 'Address', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.COUNTRY, 'Country', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.STATE, 'State', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.CITY, 'City', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.POSTAL_CODE, 'Postal Code', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.MANUAL_REVIEW, 'Manual Review', 'boolean', [
    { value: 'true', label: 'On' },
    { value: 'false', label: 'Off' },
  ]),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.SUBSIDIARY, 'Subsidiary', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.TAX_ID, 'Tax ID', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.WALLET, 'Wallet', 'text'),
  createFilterField(CUSTOMERS_COLUMNS_KEYS.ALIASES, 'Aliases', 'text'),
];
